import type { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import { useAlertContext } from '@/contexts/AlertContext'
import { ActivationGroupRouteInformation } from '@/features/activationGroup/constants'
import { useActivationGroupsQuery } from '@/features/activationGroup/hooks/useActivationGroupsQuery'
import type { ActivationGroup } from '@/features/activationGroup/types'

const RegulationObjectActivationGroupsDataGrid = ({
  countryCode,
  regulationObjectCode,
}: {
  countryCode: string
  regulationObjectCode: string
}) => {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { isLoading, isError, activationGroups } = useActivationGroupsQuery({
    countryCode: countryCode as CountryIdentifier,
  })

  useEffect(() => {
    if (isError) {
      pushAlert({
        message: t('portfolio_hierarchy.regulation_object.related_activation_group.error_loading'),
        severity: 'error',
      })
    }
  }, [isError, pushAlert, t])

  const columns: GridColDef<ActivationGroup>[] = [
    {
      field: 'uuid',
      headerName: t('portfolio_hierarchy.regulation_object.related_activation_group.uuid'),
      flex: 2,
    },
    {
      field: 'code',
      headerName: t('portfolio_hierarchy.regulation_object.related_activation_group.code'),
      flex: 3,
    },
    {
      field: 'marketProgram',
      headerName: t('portfolio_hierarchy.regulation_object.related_activation_group.market_program'),
      flex: 3,
    },
  ]

  const relatedActivationGroups =
    activationGroups?.filter((activationGroup) => regulationObjectCode === activationGroup.regulationObjectCode) ?? []

  return (
    <CustomDataGrid
      clickableRows={{
        navigateTo: ({ row }: GridRowParams<ActivationGroup>) =>
          ActivationGroupRouteInformation.ACTIVATION_GROUP_GENERAL.navigationPath(row.uuid),
      }}
      columns={columns}
      getRowId={(row: ActivationGroup) => row.uuid}
      initialState={{
        sorting: {
          sortModel: [{ field: 'marketProgram', sort: 'asc' }],
        },
      }}
      isLoading={isLoading}
      rows={relatedActivationGroups}
      slotProps={{
        root: {
          'data-testid': 'regulation-object-related-activation-groups-grid',
        },
      }}
      title={t('portfolio_hierarchy.regulation_object.related_activation_groups')}
    />
  )
}

export default RegulationObjectActivationGroupsDataGrid
